import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useRouterState, useNavigate } from "@tanstack/react-router";
import { addDays, format, parseISO } from "date-fns";
import { pl } from "date-fns/locale";
import { AirportPicker } from "./airport-picker";
import { Button } from "@/components/ui/button";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LazyCalendar as Calendar } from "@/components/ui/calendar-lazy";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeftRight, CalendarRange, CalendarDays, CalendarCheck, Loader2, Link2, Search, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ServiceClass, TripType } from "@/lib/esky";
import { validateSearchForm, type SearchFieldKey } from "@/lib/validate-search";
import { normalizePaxCount, normalizeInfants, PAX_RANGES } from "@/lib/normalize-pax";
import { defaultDestinationFor, popularDestinationsFor, popularOriginsFor } from "@/lib/seo-routes";
import { useTopDestinations } from "@/hooks/use-top-destinations";
import { useSearchedDestinations } from "@/hooks/use-searched-destinations";
import { getSessionId } from "@/lib/analytics";
import { expandRange, parseDateList, serializeDateList } from "@/lib/multi-date";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatShortDatePl } from "@/lib/format-search-date";

type DateMode = "single" | "range" | "multi";

/** On mobile the calendar is a fixed sheet, so lock page scroll while it is open.
 *  Closing on scroll made Radix's auto-positioning immediately collapse it. */
function useCloseCalendarOnScroll(open: boolean, setOpen: (o: boolean) => void) {
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 768px)").matches) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, setOpen]);
}

interface Props {
  initial?: Partial<{
    tripType: TripType; from: string; to: string;
    departDate: string; returnDate: string;
    adults: number; children: number; infants: number;
    serviceClass: ServiceClass; directOnly: boolean;
  }>;
  // „Kopiuj link" ma sens dopiero po wyszukaniu (na /search). Na stronie
  // głównej i landingach zaśmiecał UI i kopiował link do pustego query.
  showCopyLink?: boolean;
}


// `today` używane jako min dla pickerów. Liczone w LOKALNEJ strefie czasowej
// użytkownika — bo „dziś” w Warszawie o 01:24 to inny dzień niż „dziś” w UTC
// (00:00 UTC = 02:00 Warszawa). Komponent NIE renderuje konkretnej daty w SSR.
import { defaultDepartIso, defaultReturnIso, ensureFutureDate } from "@/lib/search-dates";

function isoDateLocal(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const today = isoDateLocal();

export function SearchForm({ initial, showCopyLink = false }: Props) {
  const initialFrom = initial?.from ?? "WAR";
  const [tripType, setTripTypeRaw] = useState<TripType>(initial?.tripType ?? "RoundTrip");
  // Pamiętamy ostatnią datę powrotu sprzed przełączenia na OneWay, żeby przy
  // powrocie do RoundTrip nie tracić wyboru usera.
  const lastReturnRef = useRef<string>(initial?.returnDate ?? "");
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initial?.to ?? "");
  // Hub zaproponowany przez resolver (SSR/CF) — zamrażamy w momencie mountu,
  // żeby kolejne re-rendery z innym `initial.from` nie nadpisały punktu
  // odniesienia dla metryki geo_accuracy.
  const resolvedHubRef = useRef<string>(initialFrom);
  const firstChoiceLoggedRef = useRef(false);

  function handleFromChange(code: string) {
    // Pierwsza ZMIANA wartości przez usera (nie equal do propozycji) =
    // sygnał, że resolver się pomylił. Wysyłamy raz na sesję landingu.
    if (
      !firstChoiceLoggedRef.current &&
      code &&
      code !== resolvedHubRef.current
    ) {
      firstChoiceLoggedRef.current = true;
      // Lazy import — żeby nie ładować server-fn stub w SSR initial bundle.
      import("@/lib/log-geo-accuracy.functions")
        .then(({ logGeoAccuracy }) =>
          logGeoAccuracy({
            data: {
              session_id: getSessionId(),
              resolved_hub: resolvedHubRef.current,
              chosen_hub: code,
              page:
                typeof window !== "undefined" ? window.location.pathname : "/",
              source: "first_change",
            },
          }),
        )
        .catch(() => {});
    }
    setFrom(code);
  }
  // Daty: SSR i pierwszy render klienta = "" (placeholder). Po hydratacji
  // useEffect ustawia wartości wyliczone z UTC `now` — zero hydration mismatch
  // nawet jeśli SSR-cache jest z innego dnia niż klient (np. tuż po północy).
  const [departDate, setDepart] = useState(initial?.departDate ?? "");
  const [returnDate, setReturn] = useState(initial?.returnDate ?? "");
  // Multi-date: dodatkowe wybrane daty (poza pierwszą). Pusta tablica = single.
  const initialDepartList = useMemo(() => parseDateList(initial?.departDate), [initial?.departDate]);
  const initialReturnList = useMemo(() => parseDateList(initial?.returnDate), [initial?.returnDate]);
  const [extraDeparts, setExtraDeparts] = useState<string[]>(() => initialDepartList.slice(1));
  const [extraReturns, setExtraReturns] = useState<string[]>(() => initialReturnList.slice(1));
  // Tryb wyboru dat — odgadywany z initial (range gdy ciągłe, multi w innym wypadku).
  const [dateMode, setDateMode] = useState<DateMode>(() => {
    const all = initialDepartList.length + initialReturnList.length;
    if (all <= 2) return "single";
    const dCont = initialDepartList.length > 1 && expandRange(initialDepartList[0], initialDepartList[initialDepartList.length - 1]).length === initialDepartList.length;
    const rCont = initialReturnList.length > 1 && expandRange(initialReturnList[0], initialReturnList[initialReturnList.length - 1]).length === initialReturnList.length;
    if (dCont && (initialReturnList.length <= 1 || rCont)) return "range";
    return "multi";
  });
  useEffect(() => {
    const now = new Date();
    // Daty startują puste — user musi je sam wybrać. Tylko gdy `initial`
    // dostarczył datę (np. z URL/SSR), normalizujemy ją do przyszłości.
    if (initial?.departDate) setDepart(ensureFutureDate(initialDepartList[0] ?? initial.departDate, now));
    if (initial?.returnDate) setReturn(ensureFutureDate(initialReturnList[0] ?? initial.returnDate, now));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedDeparts = useMemo(
    () => (dateMode === "single" ? (departDate ? [departDate] : []) : Array.from(new Set([departDate, ...extraDeparts].filter(Boolean))).sort()),
    [dateMode, departDate, extraDeparts],
  );
  const selectedReturns = useMemo(
    () => (dateMode === "single" ? (returnDate ? [returnDate] : []) : Array.from(new Set([returnDate, ...extraReturns].filter(Boolean))).sort()),
    [dateMode, returnDate, extraReturns],
  );
  // Po zmianie trybu chcemy żeby popover dat pozostał "otwarty" — komponent
  // jednak się przemontowuje (DateRangeField ↔ MultiDateField), więc używamy
  // flagi consumed-on-mount: nowy field samoczynnie otworzy popover.
  const [pendingDateOpen, setPendingDateOpen] = useState(false);
  // Zmiana trybu — doreguluj listy żeby UI był spójny.
  function changeDateMode(next: DateMode) {
    if (next === dateMode) return;
    if (next === "single") {
      setExtraDeparts([]);
      setExtraReturns([]);
    } else if (next === "range") {
      // single → range: domyślnie depart..depart+2 i return..return+2
      const plusDays = (iso: string, n: number): string => {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
      };
      if (extraDeparts.length === 0 && departDate) {
        const exp = expandRange(departDate, plusDays(departDate, 2));
        setExtraDeparts(exp.slice(1));
      }
      if (tripType === "RoundTrip" && extraReturns.length === 0 && returnDate) {
        const exp = expandRange(returnDate, plusDays(returnDate, 2));
        setExtraReturns(exp.slice(1));
      }
    }
    setDateMode(next);
  }
  // Zmiana typu trasy resetuje/dopasowuje pole `returnDate`, żeby nie trzymać
  // niespójnego stanu (np. powrotu wcześniejszego niż wylot po zmianie wylotu)
  // ani nie blokować submitu pustym `returnDate` po przełączeniu z OneWay.
  function setTripType(next: TripType) {
    setTripTypeRaw((prev) => {
      if (prev === next) return prev;
      if (next === "OneWay") {
        // Zapamiętaj wybór, ale wyczyść — walidacja nie sprawdza powrotu w OneWay.
        if (returnDate) lastReturnRef.current = returnDate;
        setReturn("");
      } else {
        // RoundTrip: przywróć ostatni powrót, jeśli jest sensowny (>= depart),
        // w przeciwnym razie domyślnie depart + 7 dni.
        const restored = lastReturnRef.current;
        const valid = restored && departDate && restored >= departDate;
        if (valid) {
          setReturn(restored);
        } else if (departDate) {
          // depart + 7 dni — czysto stringowo na YYYY-MM-DD, bez ryzyka TZ.
          const [y, m, d] = departDate.split("-").map(Number);
          const dt = new Date(Date.UTC(y, m - 1, d + 7));
          setReturn(dt.toISOString().slice(0, 10));
        } else {
          // Brak depart i nic do przywrócenia — zostawiamy pusty powrót.
          setReturn("");
        }
      }
      return next;
    });
  }
  // Liczniki pasażerów: zawsze przepuszczone przez `normalizePaxCount`,
  // żeby ułamki / NaN / złe stringi z `initial` (np. URL search params)
  // nie trafiły do stanu. Settery owijamy w normalizatory niżej.
  const [adults, setAdultsRaw] = useState(() => normalizePaxCount(initial?.adults, PAX_RANGES.adults));
  const [children, setChildrenRaw] = useState(() => normalizePaxCount(initial?.children, PAX_RANGES.children));
  const [infants, setInfantsRaw] = useState(() => normalizeInfants(initial?.infants, normalizePaxCount(initial?.adults, PAX_RANGES.adults)));
  const setAdults = (v: number) => {
    const safe = normalizePaxCount(v, PAX_RANGES.adults);
    setAdultsRaw(safe);
    // Po zmniejszeniu liczby dorosłych — przyciąć niemowlęta, żeby utrzymać
    // niezmiennik infants ≤ adults.
    setInfantsRaw((prev) => normalizeInfants(prev, safe));
  };
  const setChildren = (v: number) => setChildrenRaw(normalizePaxCount(v, PAX_RANGES.children));
  const setInfants = (v: number) => setInfantsRaw(normalizeInfants(v, adults));
  const [serviceClass, setClass] = useState<ServiceClass>(initial?.serviceClass ?? "economy");
  const [directOnly, setDirectRaw] = useState(initial?.directOnly ?? false);
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const setDirect = (v: boolean) => {
    setDirectRaw(v);
    // Na stronie wyników zaktualizuj URL od razu, żeby filtr "Tylko bezpośrednie"
    // przefiltrował aktualne wyniki bez ponownego submitu formularza.
    if (currentPath === "/search") {
      navigate({
        to: "/search",
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          d: v ? 1 : 0,
          stops: v ? "direct" : (prev.stops === "direct" ? "any" : prev.stops),
          page: 1,
        })) as never,
      });
    }
  };
  const [paxOpen, setPaxOpen] = useState(false);
  const paxRef = useRef<HTMLDivElement>(null);

  // Zamykaj popover pasażerów po kliknięciu poza nim — bez tego user musi
  // ponownie kliknąć trigger, co po nawigacji daje wrażenie "popover nie działa".
  useEffect(() => {
    if (!paxOpen) return;
    function onDown(e: MouseEvent) {
      if (paxRef.current && !paxRef.current.contains(e.target as Node)) {
        setPaxOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPaxOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [paxOpen]);

  // Allow other parts of the page to set the "from" airport (e.g., Popular airports gallery)
  useEffect(() => {
    function onSetFrom(e: Event) {
      const code = (e as CustomEvent<string>).detail;
      if (typeof code === "string" && code) {
        setFrom(code);
        // Smooth scroll to top so the user sees the search form filled
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    window.addEventListener("tl:set-from", onSetFrom as EventListener);
    return () => window.removeEventListener("tl:set-from", onSetFrom as EventListener);
  }, []);

  function swap() {
    setFrom(to); setTo(from);
  }

  // Stan "Szukam…" — pokazujemy spinner i blokujemy submit od razu po kliknięciu,
  // żeby user miał natychmiastowy feedback. Reset dopiero gdy zobaczymy
  // przejście pending -> idle (a nie odpalamy resetu jeszcze zanim router
  // przejdzie w "pending", bo wtedy spinner mignąłby tylko na 1 frame).
  const [submitting, setSubmitting] = useState(false);
  const sawPendingRef = useRef(false);
  const routerStatus = useRouterState({ select: (s) => s.status });
  useEffect(() => {
    if (!submitting) { sawPendingRef.current = false; return; }
    if (routerStatus === "pending") sawPendingRef.current = true;
    if (sawPendingRef.current && routerStatus === "idle") setSubmitting(false);
  }, [submitting, routerStatus]);

  // Walidacja: liczona on-the-fly z aktualnego stanu. Po pierwszej próbie
  // submitu (`showErrors`) podświetlamy też pola — wcześniej nie chcemy
  // straszyć usera czerwonymi obwódkami.
  const [showErrors, setShowErrors] = useState(false);
  const errors = validateSearchForm({
    tripType, from, to, departDate, returnDate,
    adults, children, infants, serviceClass,
  });
  const errorByField = new Map<SearchFieldKey, string>();
  // Inline pod polem pokazujemy KRÓTKĄ wersję (bez parentetycznej podpowiedzi),
  // a pełna treść z nawiasem ląduje w liście „Popraw zaznaczone pola:".
  for (const e of errors) if (!errorByField.has(e.field)) errorByField.set(e.field, e.message.replace(/\s*\([^)]*\)\.?$/, "").replace(/\.$/, ""));
  const hasErrors = errors.length > 0;
  // Daty są puste w SSR/pierwszym renderze (hydration-safe) — wtedy nie
  // blokujemy buttona, bo useEffect zaraz wypełni domyślne wartości.
  const datesPristine = !departDate && !returnDate;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // Defense in depth — nawet jeśli stan jest już znormalizowany przez settery,
    // re-normalizujemy tuż przed wysyłką. Chroni przed regresją, gdyby ktoś
    // dodał kiedyś setter omijający normalizację (np. bezpośrednie setState
    // z testu / DevTools).
    const safeAdults = normalizePaxCount(adults, PAX_RANGES.adults);
    const safeChildren = normalizePaxCount(children, PAX_RANGES.children);
    const safeInfants = normalizeInfants(infants, safeAdults);

    const liveErrors = validateSearchForm({
      tripType, from, to, departDate, returnDate,
      adults: safeAdults, children: safeChildren, infants: safeInfants, serviceClass,
    });
    if (liveErrors.length > 0) {
      setShowErrors(true);
      toast.error("Uzupełnij formularz", { description: liveErrors[0].message });
      return;
    }
    setSubmitting(true);
    // Safety net — gdyby z jakiegoś powodu router nie przeszedł w "pending"
    // (np. nawigacja zablokowana przez beforeLoad bez throw), nie zostawiamy
    // przycisku w stanie "Szukam…" na zawsze.
    setTimeout(() => setSubmitting(false), 8000);
    const ddSerialized = dateMode === "single" ? departDate : serializeDateList(selectedDeparts);
    const rdSerialized = tripType === "RoundTrip"
      ? (dateMode === "single" ? returnDate : serializeDateList(selectedReturns))
      : undefined;
    const search = {
      ot: tripType, from, to,
      dd: ddSerialized,
      rd: rdSerialized,
      a: safeAdults, c: safeChildren, i: safeInfants,
      sc: serviceClass, d: directOnly ? 1 : 0,
    } as const;
    const targetHref = buildSearchUrl(search);
    window.location.assign(targetHref);
  }

  const totalPax = adults + children + infants;
  const dbTopForFrom = useTopDestinations(from);
  const searchedFromCity = useSearchedDestinations(from);

  const popularToCodes = useMemo(
    () => popularDestinationsFor(from, dbTopForFrom, to, searchedFromCity),
    [from, dbTopForFrom, to, searchedFromCity],
  );
  // SKĄD: priorytet dla lotnisk z miasta aktualnie wybranego (np. WAW + WMI),
  // potem reszta polskich portów, potem główne huby europejskie.
  const popularFromCodes = useMemo(() => popularOriginsFor(from), [from]);

  return (
    <form
      onSubmit={submit}
      action="/search"
      method="get"
      className="relative w-full rounded-xl border border-border dark:border-white/10 bg-card dark:bg-card/80 p-3 shadow-elevated sm:p-5 lg:p-7"
    >
      {/* Pre-hydration fallback: jeśli React jeszcze nie podpiął onSubmit,
          przeglądarka wykona natywny GET /search?... z tymi inputami. */}
      <input type="hidden" name="ot" value={tripType} />
      <input type="hidden" name="from" value={from} />
      <input type="hidden" name="to" value={to} />
      <input type="hidden" name="dd" value={dateMode === "single" ? departDate : serializeDateList(selectedDeparts)} />
      {tripType === "RoundTrip" && <input type="hidden" name="rd" value={dateMode === "single" ? returnDate : serializeDateList(selectedReturns)} />}
      <input type="hidden" name="a" value={adults} />
      <input type="hidden" name="c" value={children} />
      <input type="hidden" name="i" value={infants} />
      <input type="hidden" name="sc" value={serviceClass} />
      <input type="hidden" name="d" value={directOnly ? 1 : 0} />
      {/* Trip type */}
      <div className="mb-3 flex flex-row flex-wrap items-center gap-2 sm:mb-5 sm:gap-3">
        <div className="inline-flex rounded-full bg-secondary/70 dark:bg-white/5 p-1 text-xs lg:text-sm">
          {([
            { v: "RoundTrip", label: "W obie strony" },
            { v: "OneWay", label: "W jedną stronę" },
          ] as const).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTripType(opt.v)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition lg:px-4 lg:py-1.5 lg:text-sm ${
                tripType === opt.v
                  ? "bg-gradient-sunset text-primary-foreground shadow-glow"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="mr-auto flex items-center gap-2 text-xs lg:ml-auto lg:mr-0 lg:text-sm">
          <Switch id="direct" checked={directOnly} onCheckedChange={setDirect} />
          <Label htmlFor="direct" className="text-muted-foreground">Tylko bezpośrednie</Label>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-12 lg:gap-0 lg:items-start">
        {/* From / To with swap */}
        <div className="relative lg:col-span-5">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            <AirportPicker id="from" label="Skąd" value={from} onChange={handleFromChange} placeholder="Miasto, kraj lub kod" wrapperClassName="rounded-b-none border-b-0 pr-14 lg:rounded-none lg:rounded-l-lg lg:border-b lg:pr-10" error={showErrors ? errorByField.get("from") : undefined} excludeCode={to} popularCodes={popularFromCodes} />
            <AirportPicker
              id="to"
              label="Dokąd"
              value={to}
              onChange={setTo}
              placeholder="Miasto, kraj lub kod"
              wrapperClassName="rounded-t-none lg:rounded-none lg:rounded-r-lg lg:pl-10"
              error={showErrors ? errorByField.get("to") : undefined}
              excludeCode={from}
              popularCodes={popularToCodes}
              anywhereOption
              onAnywhere={() => {
                const code = (from || "WAW").toLowerCase();
                navigate({ to: "/gdziekolwiek/$from", params: { from: code } });
              }}
            />
          </div>
          <button
            type="button"
            onClick={swap}
            aria-label="Zamień kierunki — wylot z miejscem docelowym"
            title="Zamień kierunki"
            className={cn(
              // Jak w Skyscanner: przycisk leży na styku pól, a pola mają padding przy styku.
              "absolute z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-background",
              "bg-gradient-sunset text-primary-foreground shadow-glow transition",
              "rotate-90 hover:rotate-[270deg] hover:scale-110 lg:rotate-0 lg:hover:rotate-180",
              "right-4 top-1/2 -translate-y-1/2 lg:left-1/2 lg:right-auto lg:-translate-x-1/2",
              "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
            )}
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        </div>

        {/* Dates */}
        <div className="lg:col-span-4">
          <div className="grid grid-cols-2 gap-3 lg:gap-0">
            {tripType === "RoundTrip" ? (
              dateMode === "single" ? (
                <DateRangeField
                  from={departDate}
                  to={returnDate}
                  min={today}
                  onChange={(f, t) => {
                    setDepart(f);
                    setReturn(t);
                  }}
                  fromError={showErrors ? errorByField.get("departDate") : undefined}
                  toError={showErrors ? errorByField.get("returnDate") : undefined}
                  dateMode={dateMode}
                  onDateModeChange={(m) => { setPendingDateOpen(true); changeDateMode(m); }}
                  pendingOpen={pendingDateOpen}
                  onPendingOpenConsumed={() => setPendingDateOpen(false)}
                />
              ) : (
                <>
                  <MultiDateField
                    label="Wylot"
                    mode={dateMode}
                    primary={departDate}
                    extras={extraDeparts}
                    min={today}
                    onChange={(p, ex) => { setDepart(p); setExtraDeparts(ex); }}
                    error={showErrors ? errorByField.get("departDate") : undefined}
                    dateMode={dateMode}
                    onDateModeChange={(m) => { setPendingDateOpen(true); changeDateMode(m); }}
                    pendingOpen={pendingDateOpen}
                    onPendingOpenConsumed={() => setPendingDateOpen(false)}
                  />
                  <MultiDateField
                    label="Powrót"
                    mode={dateMode}
                    primary={returnDate}
                    extras={extraReturns}
                    min={departDate || today}
                    onChange={(p, ex) => { setReturn(p); setExtraReturns(ex); }}
                    error={showErrors ? errorByField.get("returnDate") : undefined}
                    dateMode={dateMode}
                    onDateModeChange={(m) => { setPendingDateOpen(true); changeDateMode(m); }}
                  />
                </>
              )
            ) : (
              <>
                {dateMode === "single" ? (
                  <DateField
                    label="Wylot"
                    value={departDate}
                    onChange={setDepart}
                    min={today}
                    className="lg:rounded-none"
                    error={showErrors ? errorByField.get("departDate") : undefined}
                    dateMode={dateMode}
                    onDateModeChange={(m) => { setPendingDateOpen(true); changeDateMode(m); }}
                    pendingOpen={pendingDateOpen}
                    onPendingOpenConsumed={() => setPendingDateOpen(false)}
                  />
                ) : (
                  <MultiDateField
                    label="Wylot"
                    mode={dateMode}
                    primary={departDate}
                    extras={extraDeparts}
                    min={today}
                    onChange={(p, ex) => { setDepart(p); setExtraDeparts(ex); }}
                    error={showErrors ? errorByField.get("departDate") : undefined}
                    dateMode={dateMode}
                    onDateModeChange={(m) => { setPendingDateOpen(true); changeDateMode(m); }}
                    pendingOpen={pendingDateOpen}
                    onPendingOpenConsumed={() => setPendingDateOpen(false)}
                  />
                )}
                <DateField label="Powrót" value="" onChange={() => {}} disabled className="lg:rounded-none" />
              </>
            )}
          </div>
        </div>

        {/* Passengers / class */}
        {(() => {
          const paxError = showErrors
            ? (errorByField.get("adults") ??
                errorByField.get("children") ??
                errorByField.get("infants") ??
                errorByField.get("pax"))
            : undefined;
          const paxErrorId = paxError ? "pax-error" : undefined;
          return (
            <div
              ref={paxRef}
              className={cn(
                "rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 shadow-sm lg:col-span-3 lg:ml-2 lg:px-4",
                paxError ? "border-destructive bg-destructive/5" : "border-border dark:border-white/20",
              )}
            >
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pasażerowie</Label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPaxOpen((o) => !o)}
                  className="flex h-9 w-full items-center gap-2 bg-transparent px-0 text-left text-base hover:bg-transparent lg:h-8"
                  aria-invalid={paxError ? true : undefined}
                  aria-describedby={paxErrorId}
                >
                  <Users className={cn("h-4 w-4", paxError ? "text-destructive" : "text-primary")} />
                  <span>{totalPax} {totalPax === 1 ? "pasażer" : "pasażerów"}</span>
                  <span className="ml-auto text-xs text-muted-foreground capitalize">{labelForClass(serviceClass)}</span>
                </button>
                {paxOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-72 space-y-3 rounded-xl border border-border bg-popover p-4 shadow-elevated">
                    <PaxRow label="Dorośli" hint="18+ lat" value={adults} min={1} max={9} onChange={setAdults} />
                    <PaxRow label="Dzieci" hint="poniżej 18. roku życia" value={children} min={0} max={9} onChange={setChildren} />
                    <PaxRow label="Niemowlęta" hint="poniżej 2. roku życia" value={infants} min={0} max={adults} onChange={setInfants} />
                    <div>
                      <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Klasa</Label>
                      <Select value={serviceClass} onValueChange={(v) => setClass(v as ServiceClass)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="economy">Ekonomiczna</SelectItem>
                          <SelectItem value="economy-premium">Ekonomiczna premium</SelectItem>
                          <SelectItem value="business">Biznes</SelectItem>
                          <SelectItem value="first">Pierwsza</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" className="w-full" onClick={() => setPaxOpen(false)}>Zatwierdź</Button>
                  </div>
                )}
              </div>
              {paxError && (
                <p id={paxErrorId} role="alert" className="mt-1 text-xs font-medium text-destructive">
                  {paxError}
                </p>
              )}
            </div>
          );
        })()}
      </div>

      {showErrors && hasErrors && (
        <div
          role="alert"
          aria-live="polite"
          className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p className="mb-1 font-semibold">Popraw zaznaczone pola:</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {errors.map((e, i) => (
              <li key={`${e.field}-${i}`}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button
          type="submit"
          variant="hero"
          size="xl"
          className="w-full sm:flex-1"
          disabled={submitting}
          aria-busy={submitting}
          aria-disabled={submitting}
          aria-invalid={showErrors && hasErrors ? true : undefined}
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              Szukam najlepszych ofert…
            </>
          ) : (
            <>
              <Search className="h-5 w-5" aria-hidden />
              Szukaj lotów
            </>
          )}
        </Button>
        {showCopyLink && (
        <Button
          type="button"
          variant="outline"
          size="xl"
          className="w-full sm:w-auto"
          onClick={async () => {
            // Zawsze buduj URL ze SKRÓCONYM dd/rd (from..to dla ciągłych zakresów ≥3 dni).
            const ddSerialized =
              dateMode === "single" ? departDate : serializeDateList(selectedDeparts);
            const rdSerialized =
              tripType === "RoundTrip"
                ? dateMode === "single"
                  ? returnDate
                  : serializeDateList(selectedReturns)
                : undefined;
            const path = buildSearchUrl({
              ot: tripType,
              from,
              to,
              dd: ddSerialized,
              rd: rdSerialized,
              a: adults,
              c: children,
              i: infants,
              sc: serviceClass,
              d: directOnly ? 1 : 0,
            });
            const absolute =
              typeof window !== "undefined" ? new URL(path, window.location.origin).toString() : path;
            try {
              if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(absolute);
              } else {
                // Fallback dla starszych przeglądarek / niezabezpieczonego kontekstu.
                const ta = document.createElement("textarea");
                ta.value = absolute;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
              }
              toast.success("Skopiowano link", {
                description: absolute.length > 80 ? absolute.slice(0, 77) + "…" : absolute,
                duration: 2200,
              });
            } catch {
              toast.error("Nie udało się skopiować linku");
            }
          }}
          aria-label="Kopiuj link do tego wyszukiwania"
        >
          <Link2 className="h-5 w-5" aria-hidden />
          Kopiuj link
        </Button>
        )}
      </div>
    </form>
  );
}

function labelForClass(c: ServiceClass) {
  return c === "economy" ? "Ekonomiczna" : c === "economy-premium" ? "Ekonomiczna premium" : c === "business" ? "Biznes" : "Pierwsza";
}

function buildSearchUrl(search: {
  ot: TripType;
  from: string;
  to: string;
  dd: string;
  rd?: string;
  a: number;
  c: number;
  i: number;
  sc: ServiceClass;
  d: 0 | 1;
}) {
  const params = new URLSearchParams();
  params.set("ot", search.ot);
  params.set("from", search.from);
  params.set("to", search.to);
  params.set("dd", search.dd);
  if (search.rd) params.set("rd", search.rd);
  params.set("a", String(search.a));
  params.set("c", String(search.c));
  params.set("i", String(search.i));
  params.set("sc", search.sc);
  params.set("d", String(search.d));
  return `/search?${params.toString()}`;
}

function ModeTabs({ mode, onChange, onClose }: { mode: DateMode; onChange: (m: DateMode) => void; onClose?: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-secondary/40 dark:bg-black/20 px-3 py-2">
      {([
        { v: "single" as const, label: "Dzień", icon: CalendarCheck },
        { v: "range" as const, label: "Zakres", icon: CalendarRange },
        { v: "multi" as const, label: "Wiele dni", icon: CalendarDays },
      ]).map((opt) => {
        const Icon = opt.icon;
        const active = mode === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition lg:text-xs",
              active
                ? "bg-gradient-sunset text-primary-foreground shadow-glow"
                : "bg-background text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={active}
          >
            <Icon className="h-3 w-3" />
            {opt.label}
          </button>
        );
      })}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij"
          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/30 hover:text-foreground md:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function DateField({ label, value, onChange, min, disabled, className, error, dateMode, onDateModeChange, pendingOpen, onPendingOpenConsumed }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  dateMode?: DateMode;
  onDateModeChange?: (m: DateMode) => void;
  pendingOpen?: boolean;
  onPendingOpenConsumed?: () => void;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  useCloseCalendarOnScroll(open, setOpen);
  useEffect(() => {
    if (pendingOpen && !disabled) {
      setOpen(true);
      onPendingOpenConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const date = value ? parseISO(value) : undefined;
  const minDate = min ? parseISO(min) : undefined;
  const display = date ? format(date, "d MMM yyyy", { locale: pl }) : "Wybierz datę";
  const errorId = error ? `date-${label.toLowerCase()}-error` : undefined;

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={cn(
            "flex h-auto w-full flex-col items-start justify-center rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 text-left shadow-sm transition hover:bg-secondary dark:hover:bg-black/40 disabled:cursor-not-allowed disabled:opacity-40",
            error ? "border-destructive bg-destructive/5" : "border-border dark:border-white/20",
            className,
          )}
        >
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <span className={cn("flex h-9 items-center text-base lg:h-8", !date && "text-muted-foreground")}>
            {display}
          </span>
          {error && (
            <span id={errorId} role="alert" className="mt-1 text-xs font-medium text-destructive">
              {error}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="calendar-popover w-auto p-0" align="start" side={isMobile ? "bottom" : "bottom"} collisionPadding={8} sticky="always" hideWhenDetached>
        {dateMode && onDateModeChange && (
          <ModeTabs mode={dateMode} onChange={onDateModeChange} onClose={() => setOpen(false)} />
        )}
        <div className="calendar-scroll-area">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              if (d) {
                onChange(format(d, "yyyy-MM-dd"));
                setOpen(false);
              }
            }}
            disabled={(d) => (minDate ? d < minDate : false)}
            locale={pl}
            initialFocus
            className={cn("p-3 pointer-events-auto mx-auto")}
          />
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange("")}
          >
            Wyczyść
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Gotowe
          </Button>
        </div>
       </PopoverContent>
    </Popover>
  );
}

function DateRangeField({ from, to, min, onChange, fromError, toError, dateMode, onDateModeChange, pendingOpen, onPendingOpenConsumed }: {
  from: string;
  to: string;
  min?: string;
  onChange: (from: string, to: string) => void;
  fromError?: string;
  toError?: string;
  dateMode?: DateMode;
  onDateModeChange?: (m: DateMode) => void;
  pendingOpen?: boolean;
  onPendingOpenConsumed?: () => void;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  useCloseCalendarOnScroll(open, setOpen);
  useEffect(() => {
    if (pendingOpen) {
      setOpen(true);
      onPendingOpenConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [activeField, setActiveField] = useState<"from" | "to">("from");
  const [rangeError, setRangeError] = useState<string | null>(null);
  const fromBtnRef = useRef<HTMLButtonElement>(null);
  const toBtnRef = useRef<HTMLButtonElement>(null);
  const lastTriggerRef = useRef<"from" | "to">("from");
  const fromDate = from ? parseISO(from) : undefined;
  const toDate = to ? parseISO(to) : undefined;
  const minDate = min ? parseISO(min) : undefined;
  const fmt = (d?: Date) => (d ? format(d, "d MMM yyyy", { locale: pl }) : "Wybierz datę");

  // Limity biznesowe wyboru zakresu dat:
  //  - linie lotnicze sprzedają zwykle do ~330 dni w przód,
  //  - praktyczna max długość podróży round-trip = 30 dni.
  const MAX_BOOK_AHEAD_DAYS = 330;
  const MAX_TRIP_DAYS = 30;
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxBookDate = useMemo(() => addDays(today, MAX_BOOK_AHEAD_DAYS), [today]);

  // Wyczyść komunikat błędu po zmianie wyboru z zewnątrz lub po zamknięciu.
  useEffect(() => { setRangeError(null); }, [from, to, open]);

  function handleDayClick(day: Date) {
    const dStr = format(day, "yyyy-MM-dd");
    // Walidacja: data poza dopuszczalnym oknem rezerwacji.
    if (day > maxBookDate) {
      setRangeError(`Można rezerwować maksymalnie ${MAX_BOOK_AHEAD_DAYS} dni naprzód.`);
      return;
    }
    if (minDate && day < minDate) {
      setRangeError("Wybrana data jest wcześniejsza niż dozwolony minimalny termin.");
      return;
    }
    if (activeField === "from") {
      // Ustaw/zmień wylot. Jeśli istnieje już powrót późniejszy niż nowy
      // wylot — zachowaj go (pozwala wydłużyć/skrócić zakres jednym
      // kliknięciem). W przeciwnym razie wyzeruj zakres do jednego dnia.
      if (toDate && day <= toDate) {
        onChange(dStr, to);
        setRangeError(null);
        setActiveField("to");
      } else {
        onChange(dStr, dStr);
        setRangeError(null);
        setActiveField("to");
      }
    } else {
      // Setting return
      if (fromDate && day < fromDate) {
        // earlier than departure — auto-correct: treat as new departure
        toast.info("Powrót nie może być przed wylotem", {
          description: `Ustawiłem nowy wylot na ${format(day, "d MMM yyyy", { locale: pl })}. Wybierz teraz datę powrotu.`,
        });
        onChange(dStr, dStr);
        setRangeError(null);
        setActiveField("to");
        return;
      }
      // Brak limitu długości pobytu — użytkownik wybiera dowolny zakres.
      onChange(from, dStr);
      setRangeError(null);
      setOpen(false);
      setActiveField("from");
    }
  }

  function openWith(field: "from" | "to") {
    // Drugi klik na ten sam trigger zamyka picker.
    if (open && activeField === field) {
      setOpen(false);
      return;
    }
    lastTriggerRef.current = field;
    setActiveField(field);
    setOpen(true);
  }

  function handleOpenChange(o: boolean) {
    setOpen(o);
    if (!o) {
      // Restore focus to the trigger that opened the popover
      const target = lastTriggerRef.current === "from" ? fromBtnRef.current : toBtnRef.current;
      requestAnimationFrame(() => target?.focus());
      setActiveField("from");
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className="col-span-2 grid grid-cols-subgrid gap-3 lg:gap-0">
          <PopoverTrigger asChild>
            <button
              ref={fromBtnRef}
              type="button"
              onClick={() => openWith("from")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openWith("from"); } }}
              aria-invalid={fromError ? true : undefined}
              aria-describedby={fromError ? "daterange-from-error" : undefined}
              className={cn(
                "flex h-auto w-full flex-col items-start justify-center rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 text-left shadow-sm transition hover:bg-secondary dark:hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset lg:rounded-r-none",
                fromError ? "border-destructive bg-destructive/5" : "border-border dark:border-white/20",
                open && activeField === "from" && !fromError && "border-primary bg-secondary dark:bg-black/40",
              )}
            >
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Wylot</span>
              <span className={cn("flex h-9 items-center text-base lg:h-8", !fromDate && "text-muted-foreground")}>{fmt(fromDate)}</span>
              {fromError && (
                <span id="daterange-from-error" role="alert" className="mt-1 text-xs font-medium text-destructive">
                  {fromError}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverTrigger asChild>
            <button
              ref={toBtnRef}
              type="button"
              onClick={() => openWith("to")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openWith("to"); } }}
              aria-invalid={toError ? true : undefined}
              aria-describedby={toError ? "daterange-to-error" : undefined}
              className={cn(
                "flex h-auto w-full flex-col items-start justify-center rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 text-left shadow-sm transition hover:bg-secondary dark:hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset lg:rounded-l-none",
                toError ? "border-destructive bg-destructive/5" : "border-border dark:border-white/20",
                open && activeField === "to" && !toError && "border-primary bg-secondary dark:bg-black/40",
              )}
            >
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Powrót</span>
              <span className={cn("flex h-9 items-center text-base lg:h-8", !toDate && "text-muted-foreground")}>{fmt(toDate)}</span>
              {toError && (
                <span id="daterange-to-error" role="alert" className="mt-1 text-xs font-medium text-destructive">
                  {toError}
                </span>
              )}
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="calendar-popover w-auto p-0"
        align="center"
        side={isMobile ? "bottom" : "bottom"}
        collisionPadding={8}
        sticky="always"
        hideWhenDetached
        onKeyDown={(e) => {
          if (e.key === "Enter" && fromDate && toDate && (e.target as HTMLElement).tagName !== "BUTTON") {
            e.preventDefault();
            setOpen(false);
            setActiveField("from");
          }
        }}
      >
        {dateMode && onDateModeChange && (
          <ModeTabs mode={dateMode} onChange={onDateModeChange} onClose={() => setOpen(false)} />
        )}
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-sm">
          <span className="text-muted-foreground">Wybierasz:</span>
          <span className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider",
            activeField === "from"
              ? "bg-primary text-primary-foreground shadow-glow"
              : "bg-accent text-accent-foreground"
          )}>
            {activeField === "from" ? "Wylot" : "Powrót"}
          </span>
        </div>
        {rangeError && (
          <div
            role="alert"
            aria-live="polite"
            className="border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
          >
            {rangeError}
          </div>
        )}
        <div className="calendar-scroll-area">
          <Calendar
            // Force remount whenever the range changes so the calendar always
            // reflects the latest selection (fixes stale range visualization
            // when user picks a new departure that shifts the return date).
            key={`${from}-${to}-${activeField}`}
            mode="range"
            selected={fromDate ? { from: fromDate, to: toDate } : undefined}
            onDayClick={handleDayClick}
            disabled={(d) => {
              if (minDate && d < minDate) return true;
              if (d > maxBookDate) return true;
              if (activeField === "to" && fromDate && d < fromDate) return true;
              
              return false;
            }}
            numberOfMonths={isMobile ? 1 : 2}
            locale={pl}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
            modifiersClassNames={{
              range_start: "!bg-primary !text-primary-foreground ring-2 ring-primary/60 ring-offset-2 ring-offset-background rounded-md",
              range_end: "!bg-accent !text-accent-foreground ring-2 ring-accent-foreground/40 ring-offset-2 ring-offset-background rounded-md",
              range_middle: "!bg-primary/20 !text-foreground rounded-none",
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-border px-3 py-1.5 text-xs text-muted-foreground sm:justify-between sm:py-2">
          <div className="hidden flex-wrap gap-x-3 gap-y-1 sm:flex">
            <span><kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">↵</kbd> Potwierdź</span>
            <span><kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> Zamknij</span>
            <span><kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">←→</kbd> Nawiguj</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { onChange("", ""); setActiveField("from"); }}
            >
              Wyczyść
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => { setOpen(false); setActiveField("from"); }}
            >
              Gotowe
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PaxRow({ label, hint, value, min, max, onChange }: {
  label: string; hint: string; value: number; min: number; max: number; onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
          className="h-8 w-8 rounded-full border border-border text-lg leading-none hover:border-primary disabled:opacity-30"
          disabled={value <= min}>−</button>
        <span className="w-6 text-center font-mono">{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
          className="h-8 w-8 rounded-full border border-border text-lg leading-none hover:border-primary disabled:opacity-30"
          disabled={value >= max}>+</button>
      </div>
    </div>
  );
}

/**
 * MultiDateField — picker dla trybów "range" i "multi".
 *
 * Stan zewnętrzny: `primary` (pierwsza wybrana data, dla kompatybilności
 * z istniejącą walidacją) + `extras` (pozostałe wybrane dni). Razem tworzą
 * pełną listę wybranych dat; setter `onChange(primary, extras)` aktualizuje
 * obie wartości naraz.
 *
 * Etykieta na triggerze:
 *   - 1 data        → "15 maj"
 *   - range         → "15–22 maj"
 *   - multi         → "15 maj +"
 */
function MultiDateField({
  label,
  mode,
  primary,
  extras,
  min,
  onChange,
  error,
  dateMode,
  onDateModeChange,
  pendingOpen,
  onPendingOpenConsumed,
}: {
  label: string;
  mode: "range" | "multi";
  primary: string;
  extras: string[];
  min?: string;
  onChange: (primary: string, extras: string[]) => void;
  error?: string;
  dateMode?: DateMode;
  onDateModeChange?: (m: DateMode) => void;
  pendingOpen?: boolean;
  onPendingOpenConsumed?: () => void;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  useCloseCalendarOnScroll(open, setOpen);
  useEffect(() => {
    if (pendingOpen) {
      setOpen(true);
      onPendingOpenConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const all = useMemo(
    () => Array.from(new Set([primary, ...extras].filter(Boolean))).sort(),
    [primary, extras],
  );
  const dateObjs = useMemo(
    () => all.map((iso) => parseISO(iso)),
    [all],
  );
  const minDate = min ? parseISO(min) : undefined;
  const errorId = error ? `multi-${label.toLowerCase()}-error` : undefined;

  const display = useMemo(() => {
    if (all.length === 0) return "Wybierz daty";
    if (all.length === 1) return formatShortDatePl(all[0]);
    if (mode === "range") {
      const a = formatShortDatePl(all[0]);
      const b = formatShortDatePl(all[all.length - 1]);
      const aDay = a.split(" ")[0];
      return `${aDay}–${b}`;
    }
    return `${formatShortDatePl(all[0])} +`;
  }, [all, mode]);

  const summary = useMemo(() => {
    if (all.length <= 1) return null;
    if (mode === "range") return `${all.length} dni`;
    return `${all.length} wybranych`;
  }, [all, mode]);

  function commit(next: string[]) {
    const sorted = Array.from(new Set(next)).sort();
    if (sorted.length === 0) {
      onChange("", []);
      return;
    }
    onChange(sorted[0], sorted.slice(1));
  }

  function handleRangeSelect(range: { from?: Date; to?: Date } | undefined) {
    if (!range?.from) { commit([]); return; }
    const f = format(range.from, "yyyy-MM-dd");
    const t = range.to ? format(range.to, "yyyy-MM-dd") : f;
    commit(expandRange(f, t));
  }
  function handleMultiSelect(days: Date[] | undefined) {
    const list = (days ?? []).map((d) => format(d, "yyyy-MM-dd"));
    commit(list);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={cn(
            "flex h-auto w-full flex-col items-start justify-center rounded-lg border bg-secondary/60 dark:bg-black/30 px-3 py-2 text-left shadow-sm transition hover:bg-secondary dark:hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            error ? "border-destructive bg-destructive/5" : "border-border dark:border-white/20",
          )}
        >
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <span className={cn("flex h-9 w-full items-center gap-2 text-base lg:h-8", all.length === 0 && "text-muted-foreground")}>
            <span className="truncate">{display}</span>
            {summary && (
              <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{summary}</span>
            )}
          </span>
          {error && (
            <span id={errorId} role="alert" className="mt-1 text-xs font-medium text-destructive">
              {error}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="calendar-popover w-auto p-0" align="start" side={isMobile ? "bottom" : "bottom"} collisionPadding={8} sticky="always" hideWhenDetached>
        {dateMode && onDateModeChange && (
          <ModeTabs mode={dateMode} onChange={onDateModeChange} onClose={() => setOpen(false)} />
        )}
        <div className="hidden border-b border-border px-3 py-2 text-xs text-muted-foreground sm:block">
          {mode === "range"
            ? "Zaznacz początek i koniec zakresu — wyszukamy każdy dzień w środku."
            : "Kliknij wybrane dni — wyszukamy każdą zaznaczoną datę."}
        </div>
        {mode === "multi" && (
          <div
            className="multi-date-chips flex shrink-0 items-center gap-1.5 overflow-x-auto overflow-y-hidden overscroll-contain border-b border-border px-3 py-2"
            aria-label="Wybrane dni"
          >
            {all.length === 0 ? (
              <span className="text-xs text-muted-foreground">Wybierz dni z kalendarza poniżej</span>
            ) : (
              all.map((iso) => (
                <button
                  key={iso}
                  type="button"
                  onClick={() => commit(all.filter((x) => x !== iso))}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/15 px-2 py-1 text-xs font-medium text-foreground hover:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Usuń ${formatShortDatePl(iso)}`}
                >
                  <span>{formatShortDatePl(iso)}</span>
                  <X className="h-3 w-3 opacity-70" aria-hidden />
                </button>
              ))
            )}
          </div>
        )}
        <div className="calendar-scroll-area">
          {mode === "range" ? (
            <Calendar
              mode="range"
              selected={dateObjs.length > 0 ? { from: dateObjs[0], to: dateObjs[dateObjs.length - 1] } : undefined}
              onSelect={handleRangeSelect}
              disabled={(d) => (minDate ? d < minDate : false)}
              numberOfMonths={isMobile ? 1 : 2}
              locale={pl}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          ) : (() => {
            const sorted = [...dateObjs].sort((a, b) => a.getTime() - b.getTime());
            const firstSelected = sorted[0];
            const lastSelected = sorted.length > 1 ? sorted[sorted.length - 1] : undefined;
            const isSameDay = (a: Date, b: Date) =>
              a.getFullYear() === b.getFullYear() &&
              a.getMonth() === b.getMonth() &&
              a.getDate() === b.getDate();
            return (
              <Calendar
                mode="multiple"
                selected={dateObjs}
                onSelect={handleMultiSelect}
                disabled={(d) => (minDate ? d < minDate : false)}
                numberOfMonths={isMobile ? 1 : 2}
                locale={pl}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
                modifiers={{
                  multiFirst: firstSelected ? (d) => isSameDay(d, firstSelected) : () => false,
                  multiLast: lastSelected ? (d) => isSameDay(d, lastSelected) : () => false,
                }}
                modifiersClassNames={{
                  selected:
                    "!bg-primary/25 !text-foreground ring-1 ring-inset ring-primary/50 rounded-md font-medium",
                  multiFirst:
                    "!bg-primary !text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background rounded-md font-bold shadow-sm",
                  multiLast:
                    "!bg-primary !text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background rounded-md font-bold shadow-sm",
                }}
                max={10}
              />
            );
          })()}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-1.5 text-xs text-muted-foreground sm:justify-between sm:py-2">
          <span className={cn("text-[11px] sm:text-xs", mode === "multi" && all.length >= 10 && "font-semibold text-primary")}>
            {mode === "multi"
              ? `${all.length} / 10 wybranych`
              : (all.length === 0 ? "Brak wybranych dni" : `${all.length} ${all.length === 1 ? "dzień" : "dni"} wybrane`)}
          </span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => commit([])}>Wyczyść</Button>
            <Button type="button" size="sm" onClick={() => setOpen(false)}>Gotowe</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
